/* =========================================================================
   ILVA GA4 dataLayer events (s360 spec, ClickUp 869ejnku8)

   Loaded by snippets/_datalayer-init.liquid, which also defines
   window.ilvaTrack and pushes the global page data. Only interaction
   listeners live here. Component-internal events (store locator popups and
   search, search drawer open, Home Lovers signup) are hooked in their own
   files via `window.ilvaTrack?.(...)` so they no-op when tracking is off.

   Event map (this file unless noted):
   - login                    the logged-out -> logged-in transition
   - menu_click               a[href] clicks in <main-menu> and footer menus
                              (menu, click_text breadcrumb, menu_item, menu_level)
   - filter_click             facet checkbox/price changes
                              (filter, filter_selection, filter_type, filter_value)
   - add_to_favorites         the wishlist's DTAILS_WISHLIST:wishlist:add event
   - click_search_drawer      .js-search-link clicks in <predictive-search>
   - view_search_drawer       predictive-search.js open() hook
   - store_search             store-locator.js SearchMixin hook
   - address_and_opening_hours  store-locator.js openPopup hooks
   - route_directions_pdp / route_directions_find_store_page / store_information /
     check_store_display / check_store_stock_status
                              generic [data-dl-event] elements (store-locator.js
                              popup links, product-delivery-info.liquid buttons)
   - create_account           the membership:signed-up document event from the
                              Bliv medlem page form
   - create_account_in_cart   the membership:signed-up document event from the
                              cart's membership overlay
   - view_promotion           [data-dl-promo] tiles scrolled into view
                              (nested GA4 ecommerce payload, once per promo)
   - select_promotion         a link/button clicked inside a [data-dl-promo] tile,
                              unless it sits in a [data-dl-promo-ignore] subtree
   - view_collection_list /   Collection list tiles, via the generic flat
     select_collection_list   [data-dl-view-event] + [data-dl-event] pair
   - popup_interaction        clicks inside a pop-up, close X excluded

   All listeners are document-level in the CAPTURE phase: the Section
   Rendering API replaces region innerHTML wholesale, Relewise and the
   wishlist inject nodes asynchronously, and component handlers may
   stopPropagation - capture-phase delegation survives all three.
   ========================================================================= */
(() => {
  if (!window.ilvaTrack) return;

  /** Collapses whitespace so multi-line markup yields clean labels. */
  const text = (el) => (el ? el.textContent.replace(/\s+/g, ' ').trim() : '');

  /** A control's visible label. ilva-button-roll.js renders the label twice -
      the second copy is aria-hidden for the roll-up animation - so reading
      textContent gives "Find dit matchFind dit match". Dropping aria-hidden
      nodes is what a screen reader does, and it leaves plain buttons
      unchanged. */
  const label = (el) => {
    if (!el) return '';
    if (!el.querySelector('[aria-hidden="true"]')) return text(el);
    const clone = el.cloneNode(true);
    clone.querySelectorAll('[aria-hidden="true"]').forEach((node) => node.remove());
    return text(clone);
  };

  /** The global page data pushed by _datalayer-init.liquid. */
  const globals = (window.dataLayer || []).find(
    (entry) => entry && typeof entry.loginStatus === 'string'
  ) || {};

  /* ---------- login (the logged-out -> logged-in transition) --------------
     New customer accounts authenticate on Shopify's domain, so the moment of
     login is not observable in the theme. What IS observable is loginStatus
     CHANGING between pageloads, so the last seen value is kept in localStorage
     and the event fires only when it flips.

     This replaced a once-per-tab sessionStorage guard, which counted "first
     logged-in page of a tab" rather than "a sign-in happened": a returning
     member opening a new tab produced a login event with no login behind it,
     inflating the GA4 count.

     With no stored value (first visit, or cleared storage) the state is
     recorded WITHOUT firing - arriving on a live session cookie is not a
     sign-in. A real sign-in is still caught, because that journey is always
     observed logged out first. Logging out re-arms it for free. */
  const LOGIN_STATE_KEY = 'ilva:dl:loginState';
  if (globals.loginStatus === 'logged in' || globals.loginStatus === 'logged out') {
    try {
      const previous = localStorage.getItem(LOGIN_STATE_KEY);
      const current = globals.loginStatus;
      if (current === 'logged in' && previous === 'logged out') window.ilvaTrack('login');
      if (current !== previous) localStorage.setItem(LOGIN_STATE_KEY, current);
    } catch (e) {
      /* storage blocked - skip rather than fire login on every pageload */
    }
  }

  /* ---------- create_account / create_account_in_cart ---------------------
     Two names, per s360's documentation (revised 2026-09-16; ClickUp
     869ejnku8 comment 2026-09-23) - do not merge them back into the GA4 PDF's
     single "create_account" (p.7):
     - create_account          a Home Lovers sign-up on the Bliv medlem PAGE
                               (signup_method 'home_lovers_page')
     - create_account_in_cart  a Home Lovers sign-up in the cart's membership
                               overlay - any page, since the cart drawer opens it
     signup_method names the source so another create_account source can join
     without renaming the event.

     NOT reported: a new account made via the header account icon. That
     sign-in runs on Shopify's domain (<shopify-account>), and Liquid's customer
     object has no creation date, so on the page back a brand-new account looks
     exactly like a returning sign-in - both report `login` above. Telling them
     apart needs Shopify's server-side "customer created" event (a Shopify Flow
     stamping a customer metafield the theme could read, or s360 consuming the
     customers/create webhook). Offered to the client 2026-09-28, not built.

     Both sign-up forms announce success with the same document event - the ILVA
     form block (home-lovers-form.js) and the Shopify Forms watcher in
     membership-signup.js - so the event is reported from the signal rather than
     from inside either form. It used to be pushed by the overlay's
     markSignedUp(), which tied tracking to <membership-overlay> being in the
     DOM and to a guard seeded from the home_lovers_signup CART ATTRIBUTE, so a
     cart that already carried the attribute suppressed the event permanently.
     The form block says where it sits in detail.context ('page' | 'overlay');
     the Forms watcher sends no detail and only runs inside the overlay, so no
     context means the cart event.

     Note the deliberate asymmetry with login above: if storage is blocked this
     fires anyway. A duplicate conversion beats a missing one. */
  const CREATE_ACCOUNT_KEY = 'ilva:dl:createAccount';
  const CREATE_ACCOUNT_IN_CART_KEY = 'ilva:dl:createAccountInCart';
  document.addEventListener('membership:signed-up', (evt) => {
    const onPage = !!(evt.detail && evt.detail.context === 'page');
    const onceKey = onPage ? CREATE_ACCOUNT_KEY : CREATE_ACCOUNT_IN_CART_KEY;
    try {
      if (sessionStorage.getItem(onceKey)) return;
      sessionStorage.setItem(onceKey, '1');
    } catch (e) {
      /* storage blocked - fall through and report it */
    }
    if (onPage) {
      window.ilvaTrack('create_account', {
        signup_location: window.location.pathname,
        signup_method: 'home_lovers_page'
      });
    } else {
      window.ilvaTrack('create_account_in_cart', { signup_location: window.location.pathname });
    }
  });

  /** dataset keys that NAME the event or key its dedupe rather than describing
      it — they must not ride along as dataLayer params. */
  const DL_CONTROL_KEYS = ['dlEvent', 'dlViewEvent', 'dlViewId'];

  /** dataset key "dlInStock" -> dataLayer param "in_stock". */
  const datasetParams = (el) => {
    const params = {};
    Object.keys(el.dataset).forEach((key) => {
      if (DL_CONTROL_KEYS.includes(key) || !key.startsWith('dl')) return;
      const name = key
        .slice(2)
        .replace(/[A-Z]/g, (m) => `_${m.toLowerCase()}`)
        .replace(/^_/, '');
      params[name] = el.dataset[key];
    });
    return params;
  };

  /** "Produkter > Borde > Sengeborde" from the nav DOM ancestry. Each nested
      panel (.main-nav__child at the top level, .child-nav__panel at the
      grandchild level) is owned by an <li> whose first anchor is the parent
      item - works for both the desktop and mobile nav trees. */
  const menuBreadcrumb = (link) => {
    const parts = [text(link)];
    let node = link.parentElement;
    while (node && node.tagName !== 'MAIN-MENU') {
      if (node.classList && (node.classList.contains('main-nav__child') || node.classList.contains('child-nav__panel'))) {
        const ownerLi = node.closest('li');
        const ownerLink = ownerLi && ownerLi.querySelector('a[href]');
        const ownerText = text(ownerLink);
        if (ownerText && ownerText !== parts[0]) parts.unshift(ownerText);
        node = ownerLi || node.parentElement;
        continue;
      }
      node = node.parentElement;
    }
    return parts.filter(Boolean).join(' > ');
  };

  /* ---------- GA4 promotion events ----------------------------------------
     Content tiles report as GA4 promotions (ClickUp comment 2026-09-02):
     view_promotion when the tile is seen, select_promotion when it is
     clicked, as independent events so exposure and interaction can be told
     apart. The GA4 spec puts these in a nested `ecommerce` object and
     requires a non-empty `items` array, so they go through
     ilvaTrackEcommerce rather than the flat ilvaTrack. The sheet's flat
     wishes (tile_name, tile_position, destination_url) ride alongside the
     GA4 params, so whichever set s360 built their tags on resolves. */

  /** [data-dl-promo-*] attributes -> the GA4 ecommerce payload + flat extras. */
  const promoPayload = (el) => {
    const d = el.dataset;
    const id = d.dlPromoId || '';
    const name = d.dlPromoName || '';
    return {
      ecommerce: {
        creative_name: d.dlPromoCreativeName || '',
        creative_slot: d.dlPromoCreativeSlot || '',
        promotion_id: id,
        promotion_name: name,
        // items is mandatory on both promotion events. A tile with a linked
        // product reports that product; a purely editorial tile reports one
        // item mirroring the promotion so the event stays spec-valid.
        items: [{
          item_id: d.dlPromoItemId || id,
          item_name: d.dlPromoItemName || name
        }]
      },
      extras: {
        tile_name: name,
        tile_position: d.dlPromoPosition || ''
      }
    };
  };

  /* One impression per promotion_id per pageload. The id (not the node)
     is the key because Relewise empties its grid and re-clones every promo
     on each render - initial load, filter change, pagination - so the same
     tile arrives as a fresh element repeatedly. The same set serves the flat
     [data-dl-view-event] tiles below, keyed "<event>|<data-dl-view-id>", so
     an id can never collide across the two payload shapes. */
  const seenPromos = new Set();
  let promoObserver = null;

  /* The observed selector covers two payload shapes on purpose.

     [data-dl-promo] is the GA4 promotion shape: nested `ecommerce`, through
     ilvaTrackEcommerce. [data-dl-view-event] is a FLAT event through
     ilvaTrack, named by the markup and parameterised straight from its own
     data-dl-* attributes - which is what lets Collection list report
     view_collection_list/select_collection_list (ClickUp comment
     90120264940256) without a second observer, a second threshold ladder or a
     second MutationObserver. A tile carries one shape or the other, never
     both. */
  const PROMO_SELECTOR = '[data-dl-promo], [data-dl-view-event]';

  /* Thresholds are a ladder rather than a single 0.5, because intersectionRatio
     is visible-height / ELEMENT-height: a promo taller than twice the viewport
     can never reach 0.5, and with one threshold the callback is never invoked
     again after the tile enters, so the impression is lost silently while the
     click still reports. Measured on the campaign banner (1392px tall): fires
     at 375x812, never at 375x667. Half the SCREEN covered by the tile carries
     the same "properly seen" intent and stays reachable at any element size,
     so either rule counts. */
  const promoThresholds = [];
  for (let t = 0; t <= 1.0001; t += 0.05) promoThresholds.push(Math.min(1, t));

  if ('IntersectionObserver' in window) {
    promoObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const seen = entry.intersectionRatio >= 0.5
          || entry.intersectionRect.height >= window.innerHeight * 0.5;
        if (!seen) return;
        promoObserver.unobserve(entry.target);

        const viewEvent = entry.target.dataset.dlViewEvent;
        if (viewEvent) {
          const viewKey = `${viewEvent}|${entry.target.dataset.dlViewId || ''}`;
          if (seenPromos.has(viewKey)) return;
          seenPromos.add(viewKey);
          window.ilvaTrack(viewEvent, datasetParams(entry.target));
          return;
        }

        /* Dedupe on the intrinsic id, not on promotion_id. Merchants can now
           type promotion_id in the theme editor, and reusing one campaign id
           across two placements is exactly what that field is for - keyed on
           promotion_id the second tile's view_promotion would be swallowed.
           The dedupe id stays unique per tile (block.id, or the composed
           block.id-link-slot the menu promos build), so a Relewise re-clone
           still carries the same key and stays deduped. */
        const payload = promoPayload(entry.target);
        const dedupeKey = entry.target.dataset.dlPromoDedupeId
          || payload.ecommerce.promotion_id;
        if (dedupeKey && seenPromos.has(dedupeKey)) return;
        if (dedupeKey) seenPromos.add(dedupeKey);
        window.ilvaTrackEcommerce('view_promotion', payload.ecommerce, payload.extras);
      });
    }, { threshold: promoThresholds });
  }

  /* Promos inside a <template> are never reached: their markup lives in the
     template's inert content fragment, which querySelectorAll cannot see. So
     only live tiles get observed, and Relewise's clones arrive via the
     MutationObserver below. */
  const registerPromos = (root) => {
    if (!promoObserver || !(root instanceof Element)) return;
    if (root.matches(PROMO_SELECTOR)) promoObserver.observe(root);
    root.querySelectorAll(PROMO_SELECTOR).forEach((el) => promoObserver.observe(el));
  };

  /* Relewise empties the grid before rebuilding it, so a tile that was never
     scrolled to would stay observed forever - the observer keeps a strong
     reference to the detached node. Release them as they leave the DOM. */
  const unregisterPromos = (root) => {
    if (!promoObserver || !(root instanceof Element)) return;
    if (root.matches(PROMO_SELECTOR)) promoObserver.unobserve(root);
    root.querySelectorAll(PROMO_SELECTOR).forEach((el) => promoObserver.unobserve(el));
  };

  if (promoObserver) {
    registerPromos(document.body);
    // <body> is the one ancestor guaranteed to outlive every grid re-render;
    // childList only, so the callback cost stays negligible.
    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => registerPromos(node));
        mutation.removedNodes.forEach((node) => unregisterPromos(node));
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  document.addEventListener('click', (evt) => {
    if (!(evt.target instanceof Element)) return;

    /* ---------- generic [data-dl-event] elements ----------
       The attribute sits on the control itself at every phase-1 site (store
       locator popup links, product-delivery-info buttons), and Collection list
       is the first CONTAINER to carry it - the tile is an <li> wrapping a card
       whose padding is not a control. So when the host is not itself a link or
       button, require the click to have landed on one inside it; clicking the
       gap around the card is not selecting it. Every pre-existing site is a
       link or button and takes the first branch unchanged. */
    const dlEl = evt.target.closest('[data-dl-event]');
    if (dlEl) {
      const dlAction = dlEl.matches('a[href], button') ? dlEl : evt.target.closest('a[href], button');
      if (dlAction && dlEl.contains(dlAction)) {
        window.ilvaTrack(dlEl.dataset.dlEvent, datasetParams(dlEl));
      }
    }

    /* ---------- select_promotion ----------
       Only an actual link or button counts as selecting the promotion -
       campaign-feature's tile spans a whole column of body copy, and a click
       on that text is not an interaction with the promo.

       [data-dl-promo-ignore] carves a subtree back OUT of the promo. Product
       list and Featured collection report as one promotion at section level
       (the PDF names both), but their product grids are item-funnel surfaces:
       without this, every product-card click inside them would arrive as a
       select_promotion and drown the real promo clicks. Shoppable image uses
       it for the same reason on its product panels. */
    const promoEl = evt.target.closest('[data-dl-promo]');
    if (promoEl) {
      const action = evt.target.closest('a[href], button');
      const ignored = evt.target.closest('[data-dl-promo-ignore]');
      if (action && promoEl.contains(action) && !(ignored && promoEl.contains(ignored))) {
        const payload = promoPayload(promoEl);
        window.ilvaTrackEcommerce('select_promotion', payload.ecommerce, Object.assign({
          destination_url: action.getAttribute('href') || ''
        }, payload.extras));
      }
    }

    /* ---------- popup_interaction ----------
       The sheet asks for pop-up clicks with the close cross excluded. Two
       surfaces, so the switch to Shopify's native pop-up needs no rework
       (ClickUp comment 2026-09-02):

       1. The theme's <pop-up> section. Excluded by .modal__close-btn, NOT by
          .js-close-modal - the pop-up's own CTAs carry that class too (they
          dismiss the pop-up as they navigate), and those clicks are exactly
          what the sheet wants counted.
       2. Shopify Forms, which renders Shopify's pop-ups, inside a
          <shopify-forms-embed> host with an OPEN shadow root. A click in a
          shadow root retargets to the host, so the real node comes from
          composedPath(). The cart's Home Lovers membership form uses the same
          element and is not a pop-up, so it is excluded by its wrapper.
          Unverified until a real Forms pop-up exists - see the handoff. */
    const themePopup = evt.target.closest('pop-up');
    if (themePopup && evt.target.closest('.modal__window') && !evt.target.closest('.modal__close-btn')) {
      const dialog = themePopup.querySelector('.modal__window');
      const headingId = dialog && dialog.getAttribute('aria-labelledby');
      const heading = headingId && document.getElementById(headingId);
      window.ilvaTrack('popup_interaction', {
        popup_type: 'theme',
        popup_id: themePopup.id || '',
        popup_name: text(heading),
        click_text: label(evt.target.closest('a[href], button')) || text(evt.target),
        page_type: globals.pageType || ''
      });
    } else {
      const formsHost = evt.target.closest('shopify-forms-embed');
      if (formsHost && !formsHost.closest('[data-membership-form]')) {
        const inner = evt.composedPath().find(
          (node) => node instanceof Element && node.matches('a[href], button')
        );
        const clickText = inner ? (label(inner) || inner.getAttribute('aria-label') || '') : '';
        if (inner && !/luk|close|dismiss/i.test(clickText)) {
          window.ilvaTrack('popup_interaction', {
            popup_type: 'shopify_forms',
            popup_id: formsHost.getAttribute('data-form-id') || '',
            popup_name: '',
            click_text: clickText,
            page_type: globals.pageType || ''
          });
        }
      }
    }

    /* ---------- click_search_drawer ---------- */
    const searchLink = evt.target.closest('predictive-search .js-search-link');
    if (searchLink) {
      const drawer = searchLink.closest('predictive-search');
      const input = drawer && drawer.querySelector('.js-search-input');
      let suggestionType = 'other';
      if (searchLink.classList.contains('rlw-ps__suggestion')) suggestionType = 'suggestion';
      else if (searchLink.classList.contains('rlw-ps__product')) suggestionType = 'product';
      else if (searchLink.classList.contains('rlw-ps__location')) suggestionType = 'store';
      else if (searchLink.classList.contains('rlw-ps__article')) suggestionType = 'article';
      window.ilvaTrack('click_search_drawer', {
        search_term: input ? input.value.trim() : '',
        clicked_search_suggestion: text(searchLink),
        suggestion_type: suggestionType
      });
    }

    /* ---------- menu_click ---------- */
    const link = evt.target.closest('a[href]');
    if (link) {
      if (link.closest('main-menu')) {
        const breadcrumb = menuBreadcrumb(link);
        window.ilvaTrack('menu_click', {
          menu: 'Main menu',
          click_text: breadcrumb,
          menu_item: text(link),
          menu_level: breadcrumb.split(' > ').length
        });
      } else if (link.closest('.footer-menu__links, .footer-sublinks__item')) {
        const col = link.closest('.footer-col');
        const heading = text(col && col.querySelector('.disclosure__title'));
        window.ilvaTrack('menu_click', {
          menu: 'Footer menu',
          click_text: heading ? `${heading} > ${text(link)}` : text(link),
          menu_item: text(link),
          menu_level: heading ? 2 : 1
        });
      }
    }
  }, true);

  /* ---------- add_to_favorites (theme wishlist, assets/wishlist.js) --------
     The wishlist store dispatches a CustomEvent on every real add
     (DTAILS_WISHLIST:wishlist:add, mirrored by :remove - the same events
     relewise.js trusts for engagement tracking). This beats classifying
     clicks: the class flip and localStorage write lag behind the click, so
     a click-time state read misreads fast add/remove sequences.
     Only adds are tracked, per the s360 spec. */
  document.addEventListener('DTAILS_WISHLIST:wishlist:add', (evt) => {
    const handle = (evt.detail && evt.detail.productHandle) || '';
    let itemName = '';
    const escaped = handle && (window.CSS && CSS.escape ? CSS.escape(handle) : handle);
    const btn = escaped && document.querySelector(`[data-button-wishlist][data-product-handle="${escaped}"]`);
    const card = btn && btn.closest('.card');
    if (card) {
      itemName = text(card.querySelector('.card__title') || card.querySelector('.product-card-mini__title'));
    }
    if (!itemName && globals.pageType === 'PDP') {
      // The PDP's main heart sits outside any card - read the product heading.
      // Container-scoped, not tag-scoped: the heading is an h2 since 869eya2p5
      // (the page h1 moved to the product title further down), and .cc-main-product
      // keeps a bare .product-title in the quick-add drawer - or in a
      // featured-product section on the same page - from matching first. Cloned
      // with the controls stripped: the wishlist button renders INSIDE the
      // heading, whose hidden labels pollute textContent.
      const heading = document.querySelector('.cc-main-product .product-info__title .product-title');
      if (heading) {
        const clone = heading.cloneNode(true);
        clone.querySelectorAll('button, .wish__ext').forEach((el) => el.remove());
        itemName = text(clone);
      }
    }
    window.ilvaTrack('add_to_favorites', {
      item_handle: handle,
      item_name: itemName,
      page_type: globals.pageType || ''
    });
  });

  /* ---------- filter_click ------------------------------------------------
     Delegated 'change' survives the Section Rendering API re-render that
     facet-filters.js performs after every filter change. Sorting is not a
     filter: the mobile sort radios fall out of the checkbox/price branches,
     the desktop select sits outside <facet-filters>, and the sort-by id
     guard is defence in depth for future markup. */
  document.addEventListener('change', (evt) => {
    const input = evt.target;
    if (!(input instanceof HTMLInputElement)) return;
    if (!input.closest('facet-filters')) return;
    if (input.id && input.id.includes('sort-by')) return;

    const group = input.closest('details.filter');
    const filterName = text(group && group.querySelector('summary .font-bold'));
    let selection = '';
    let value = '';

    if (input.type === 'checkbox') {
      const li = input.closest('li');
      value = text(li && li.querySelector('.filter__label .flex-auto')) || input.value;
      selection = group
        ? Array.from(group.querySelectorAll('input[type="checkbox"]:checked'))
          .map((box) => {
            const boxLi = box.closest('li');
            return text(boxLi && boxLi.querySelector('.filter__label .flex-auto')) || box.value;
          })
          .join(',')
        : value;
    } else if (input.id && input.id.includes('price-range')) {
      // Price range: report the chosen min-max span.
      const bounds = group
        ? Array.from(group.querySelectorAll('input[type="number"]')).map((el) => el.value)
        : [input.value];
      selection = bounds.filter((v) => v !== '').join('-');
      value = selection;
    } else {
      return;
    }

    window.ilvaTrack('filter_click', {
      filter: filterName,
      filter_selection: selection,
      filter_type: filterName,
      filter_value: value,
      page_type: globals.pageType || ''
    });
  }, true);
})();
